// Where exported files go, on every platform the app ships on:
//   - browser / PWA: the browser's Downloads folder, or (Chromium) a save dialog / a folder the
//     user picked once through the File System Access API;
//   - macOS (Tauri): the user's Downloads folder, a native save dialog, or a chosen folder, via
//     the `save_file` / `pick_folder` commands in src-tauri/src/main.rs;
//   - iOS (Capacitor): the share sheet, which itself offers Save to Photos / Save to Files.
// Everything stays on-device; nothing here touches the network.
import { browserDownload, isNativePlatform, saveAndShare } from './platform.js';

export type SaveMode = 'downloads' | 'ask' | 'folder';
export type SavePlatform = 'web' | 'tauri' | 'capacitor';

export interface SaveResult {
  /** Human-readable destination, e.g. "Downloads", "Pictures/Cutouts", "the share sheet". */
  where: string;
}

const MODE_KEY = 'mpt.saveMode';
const FOLDER_NAME_KEY = 'mpt.saveFolderName';
const FOLDER_PATH_KEY = 'mpt.saveFolderPath'; // Tauri only: absolute path

type TauriInvoke = (cmd: string, payload?: unknown, options?: { headers?: Record<string, string> }) => Promise<unknown>;

function tauriInvoke(): TauriInvoke | null {
  const w = globalThis as unknown as { __TAURI__?: { core?: { invoke?: TauriInvoke } }; __TAURI_INTERNALS__?: { invoke?: TauriInvoke } };
  const fn = w.__TAURI__?.core?.invoke ?? w.__TAURI_INTERNALS__?.invoke;
  return typeof fn === 'function' ? fn : null;
}

export function savePlatform(): SavePlatform {
  if (isNativePlatform()) return 'capacitor';
  if (tauriInvoke()) return 'tauri';
  return 'web';
}

interface FsWindow {
  showSaveFilePicker?: (opts: { suggestedName?: string; types?: { description: string; accept: Record<string, string[]> }[] }) => Promise<FileSystemFileHandle>;
  showDirectoryPicker?: (opts: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<FileSystemDirectoryHandle>;
}
type PermissionHandle = { queryPermission?: (d: { mode: 'readwrite' }) => Promise<PermissionState>; requestPermission?: (d: { mode: 'readwrite' }) => Promise<PermissionState> };

/** Which "Save to" choices make sense here. */
export function saveCapabilities(): { platform: SavePlatform; canAsk: boolean; canFolder: boolean } {
  const platform = savePlatform();
  if (platform === 'capacitor') return { platform, canAsk: false, canFolder: false };
  if (platform === 'tauri') return { platform, canAsk: true, canFolder: true };
  const w = globalThis as unknown as FsWindow;
  return { platform, canAsk: typeof w.showSaveFilePicker === 'function', canFolder: typeof w.showDirectoryPicker === 'function' };
}

export function getSaveMode(): SaveMode {
  const caps = saveCapabilities();
  const stored = safeGet(MODE_KEY) as SaveMode | null;
  if (stored === 'ask' && caps.canAsk) return 'ask';
  if (stored === 'folder' && caps.canFolder && getSaveFolderName()) return 'folder';
  return 'downloads';
}

export function setSaveMode(mode: SaveMode): void {
  safeSet(MODE_KEY, mode);
}

export function getSaveFolderName(): string | null {
  return safeGet(FOLDER_NAME_KEY);
}

/** Lets the user pick a folder for all future exports. Resolves to its display name, or null if cancelled. */
export async function chooseSaveFolder(): Promise<string | null> {
  const platform = savePlatform();
  if (platform === 'tauri') {
    const path = (await tauriInvoke()!('pick_folder')) as string | null;
    if (!path) return null;
    safeSet(FOLDER_PATH_KEY, path);
    const name = path.split('/').filter(Boolean).slice(-2).join('/') || path;
    safeSet(FOLDER_NAME_KEY, name);
    return name;
  }
  const w = globalThis as unknown as FsWindow;
  if (!w.showDirectoryPicker) return null;
  try {
    const handle = await w.showDirectoryPicker({ mode: 'readwrite', id: 'myphototools-exports' });
    await idbSet('saveFolder', handle);
    safeSet(FOLDER_NAME_KEY, handle.name);
    return handle.name;
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return null;
    throw err;
  }
}

/**
 * Saves `blob` as `filename` wherever the user asked. Resolves to null when the user cancelled a
 * dialog (nothing was written), otherwise to where the file went.
 */
export async function saveFile(blob: Blob, filename: string): Promise<SaveResult | null> {
  const platform = savePlatform();
  if (platform === 'capacitor') {
    await saveAndShare(blob, filename);
    return { where: 'the share sheet' };
  }
  const mode = getSaveMode();
  if (platform === 'tauri') return saveViaTauri(blob, filename, mode);
  return saveViaBrowser(blob, filename, mode);
}

async function saveViaTauri(blob: Blob, filename: string, mode: SaveMode): Promise<SaveResult | null> {
  const invoke = tauriInvoke()!;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const headers: Record<string, string> = { 'x-name': encodeURIComponent(filename), 'x-mode': mode };
  const folder = safeGet(FOLDER_PATH_KEY);
  if (mode === 'folder' && folder) headers['x-folder'] = encodeURIComponent(folder);
  const path = (await invoke('save_file', bytes, { headers })) as string | null;
  if (!path) return null; // cancelled
  const parts = path.split('/').filter(Boolean);
  const dir = parts.slice(0, -1);
  const shown = dir.length > 2 ? dir.slice(-2).join('/') : dir.join('/');
  return { where: shown || path };
}

async function saveViaBrowser(blob: Blob, filename: string, mode: SaveMode): Promise<SaveResult | null> {
  const w = globalThis as unknown as FsWindow;
  if (mode === 'folder') {
    const handle = (await idbGet('saveFolder')) as (FileSystemDirectoryHandle & PermissionHandle) | undefined;
    if (handle) {
      try {
        let state: PermissionState = 'granted';
        if (handle.queryPermission) state = await handle.queryPermission({ mode: 'readwrite' });
        if (state !== 'granted' && handle.requestPermission) state = await handle.requestPermission({ mode: 'readwrite' });
        if (state === 'granted') {
          const name = await uniqueName(handle, filename);
          const fh = await handle.getFileHandle(name, { create: true });
          const writable = await fh.createWritable();
          await writable.write(blob);
          await writable.close();
          return { where: handle.name };
        }
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') return null;
        // Folder gone or writing not allowed here (e.g. a browser with only partial File System Access): fall through.
      }
    }
    // No folder any more (or permission refused): fall through to the save dialog if available.
    mode = w.showSaveFilePicker ? 'ask' : 'downloads';
  }
  if (mode === 'ask' && w.showSaveFilePicker) {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    const mime = ext === 'png' ? 'image/png' : ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : ext === 'zip' ? 'application/zip' : ext === 'pdf' ? 'application/pdf' : 'application/octet-stream';
    try {
      const fh = await w.showSaveFilePicker({ suggestedName: filename, types: [{ description: ext.toUpperCase() || 'File', accept: { [mime]: [`.${ext}`] } }] });
      const writable = await fh.createWritable();
      await writable.write(blob);
      await writable.close();
      return { where: 'the folder you picked' };
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return null;
      // The picker exists but can't write here (some Chrome on Android builds): use a plain download instead.
    }
  }
  browserDownload(blob, filename);
  return { where: "your browser's Downloads" };
}

/** "photo_nobg.png" → "photo_nobg (2).png" when the name is taken, like Finder does. */
async function uniqueName(dir: FileSystemDirectoryHandle, filename: string): Promise<string> {
  const dot = filename.lastIndexOf('.');
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  const ext = dot > 0 ? filename.slice(dot) : '';
  for (let i = 1; i < 1000; i++) {
    const candidate = i === 1 ? filename : `${stem} (${i})${ext}`;
    try {
      await dir.getFileHandle(candidate);
    } catch {
      return candidate; // not found: free
    }
  }
  return filename;
}

// ---- tiny storage helpers ----

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode: the choice just doesn't persist
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('myphototools', 1);
    req.onupgradeneeded = () => req.result.createObjectStore('kv');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('kv', 'readwrite');
    tx.objectStore('kv').put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
async function idbGet(key: string): Promise<unknown> {
  try {
    const db = await openDb();
    const value = await new Promise<unknown>((resolve, reject) => {
      const req = db.transaction('kv', 'readonly').objectStore('kv').get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return value;
  } catch {
    return undefined;
  }
}
